import useTranslator from "../../hooks/useTranslator";
import styles from "../SelfManagedGuarantorsModal/SelfManagedGuarantorsModal.module.css";

export type SelfManagedCoTenant = {
  name: string;
  email?: string;
  completed?: boolean;
};

type Props = {
  coTenants: SelfManagedCoTenant[];
  onLater: () => void;
  inline?: boolean;
};

const SendIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7">
    <path d="M22 2L11 13" />
    <path d="M22 2L15 22L11 13L2 9L22 2Z" />
  </svg>
);
const CardIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 9h20" />
    <path d="M7 15h4" />
  </svg>
);
const BriefcaseIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7">
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M8 7V5a4 4 0 0 1 8 0v2" />
    <path d="M3 12h18" />
  </svg>
);
const InfoIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 10v6" />
    <path d="M12 7h.01" />
  </svg>
);

export default function SelfManagedCoTenantsModal({ coTenants, onLater, inline }: Props) {
  const { _t } = useTranslator();
  const content = (
    <div className={styles.card} onClick={(e) => e.stopPropagation()}>
      <h2>{_t("onboarding_self_managed_title", "Create your application")}</h2>
      <p className={styles.subtitle}>
        {_t("onboarding_self_managed_co_tenant_subtitle", "Complete these tenant profiles yourself.")}
      </p>
      <div className={styles.whatNext}>
        <strong>{_t("onboarding_what_next_title", "What happens next")}</strong>
        <div className={styles.whatRow}>
          <span className={styles.iconWrap}>
            <SendIcon />
          </span>
          <div className={styles.whatText}>
            <b>{_t("onboarding_what_next_invited_title", "Invited profiles")}</b>
            <p>{_t("onboarding_what_next_invited_desc", "We'll email them automatically. They complete their own application.")}</p>
          </div>
        </div>
        <div className={styles.whatRow}>
          <span className={styles.iconWrap}>
            <CardIcon />
          </span>
          <div className={styles.whatText}>
            <b>{_t("onboarding_what_next_self_managed_title", "Profiles you'll fill in")}</b>
            <p className={styles.needFor}>{_t("onboarding_what_next_self_managed_need", "You'll need, for each person:")}</p>
            <div className={styles.needRow}>
              <CardIcon />
              <span>{_t("onboarding_what_next_need_id", "Their ID or passport")}</span>
            </div>
            <div className={styles.needRow}>
              <BriefcaseIcon />
              <span>{_t("onboarding_what_next_need_income", "Proof of work and income (payslips, contract or invoices)")}</span>
            </div>
            <div className={styles.infoRow}>
              <InfoIcon />
              <span>{_t("onboarding_what_next_consent_info", "When you complete a profile, we'll email that person to confirm you had their permission, review their details, and accept the terms before we process their information.")}</span>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.list}>
        {coTenants.map((g) => {
          const firstName = g.name.split(/\s+/)[0] || g.name;
          return (
            <div key={g.email || g.name} className={styles.row}>
              <span className={styles.avatar}>{(g.name || "G").charAt(0).toUpperCase()}</span>
              <div className={styles.meta}>
                <strong>{g.name}</strong>
                <span>{_t("tenant", "Tenant")} · {_t("co_tenant", "Co-tenant")}</span>
              </div>
              {g.completed ? (
                <span className={styles.completed}>✓ {_t("onboarding_self_managed_completed", "Completed")}</span>
              ) : (
                <button
                  type="button"
                  className={styles.start}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const pendingCount = coTenants.filter((x) => !x.completed).length;
                    const params = new URLSearchParams({ coTenantName: g.name });
                    if (g.email) {
                      params.set("email", g.email);
                      params.set("firstName", firstName);
                    }
                    if (pendingCount > 1) params.set("returnTo", "/onboarding?welcome=true");
                    params.set("showSidebar", "true");
                    try {
                      sessionStorage.setItem("selfManagedCoTenants", JSON.stringify(coTenants));
                    } catch { }
                    window.location.assign(`/co-tenant/onboarding?${params.toString()}`);
                  }}
                >
                  {_t("onboarding_self_managed_start", "Start")}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <button className={styles.later} onClick={onLater}>
        {_t("onboarding_self_managed_later", "Run them later from your dashboard")}
      </button>
    </div>
  );
  if (inline) return content;
  return (
    <div className={styles.overlay} onClick={onLater}>
      {content}
    </div>
  );
}
